import {
	Component,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { VbNestedMenuComponent } from './shared/VbNestedMenuComponent';
import { VideogularComponent } from './videogular/Videogular.Component';

import { HlsUtilService } from './HlsUtil.Service';
import { IVbSource } from './IVbSource';
import { userAgentUtil } from './UserAgentUtil';

@Component({
	selector: 'vb-playback-rate-button',
	host: {
		'[class]': 'menuButtonStyles.textMenuButtonContainer'
	},
	template: `
		<ng-container *ngIf="(playbackRateEnabled$ | async) as playbackRateEnabled">
			<div
				[ngClass]="menuButtonStyles.dropdown"
				dropdown
				#dropdown="bs-dropdown"
				[autoClose]="true"
				[insideClick]="false"
				placement="top right"
				[dropup]="true"
				(isOpenChange)="onMenuOpenChange($event)">

				<ul
					[ngClass]="menuButtonStyles.dropdownMenu"
					*dropdownMenu
					#vbDropdownMenu
					aria-labelledby="buttonMenuPlaybackRate"
					id="dropdownPlaybackRateMenu"
					role="menu"
					tabindex="0">
					<li role="menuitem">
						<a
							href="javascript:"
							class="dropdown-item"
							[ngClass]="menuButtonStyles.backToMainMenuItem"
							(click)="hideMenu()">
							<span>{{ translations.playbackSpeed }}</span>
						</a>
					</li>
					<li class="divider dropdown-divider"></li>
					<li role="menuitem"
						*ngFor="let rate of playbackRates">
						<a
							href="javascript:"
							class="dropdown-item"
							(click)="onSelectPlaybackRate(rate)"
							[ngClass]="vgApi.playback === rate ? menuButtonStyles.selected : ''">
							<span *ngIf="rate === 1">{{ translations.normal }}</span>
							<span *ngIf="rate !== 1">{{ rate | number }}x</span>
						</a>
					</li>
				</ul>
			</div>
		</ng-container>
`
})
export class VbPlaybackRateButtonComponent extends VbNestedMenuComponent implements OnChanges, OnInit {
	@Input() public activeSource: any;
	@Input() public playbackOptions: IVbSource[];

	private browserSupportsPlaybackRate: boolean;
	public isMobile: boolean;
	public readonly playbackRates: number[] = [1, 1.25, 1.5, 2];

	private playbackRateEnabledSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public playbackRateEnabled$: Observable<boolean> = this.playbackRateEnabledSubject$.asObservable();

	constructor(
		private HlsUtilService: HlsUtilService,
		public vgApi: VideogularComponent
	) {
		super();
	}

	public ngOnInit(): void {
		this.browserSupportsPlaybackRate = this.checkBrowserSupport();
		this.isMobile = userAgentUtil.isMobile;

		this.updateVideoSource();

		this.vgApi.mediaElement.addEventListener('ratechange', () => {
			//IE resets playbackRate to 1 when playing/pausing, seeking, or switching video source
			if (this.vgApi.rawMediaElement.playbackRate !== this.vgApi.playback) {
				this.applyPlaybackRate();
			}
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		super.ngOnChanges(changes);

		if (changes.playbackOptions) {
			this.onPlaybackOptionsChange();
		}

		if (changes.activeSource) {
			this.onActiveSourceChange();
		}
	}

	public applyPlaybackRate(): void {
		this.setPlaybackRate(this.vgApi.playback);
	}

	private checkBrowserSupport(): boolean {
		const video: HTMLVideoElement = document.createElement('video');

		return typeof video.playbackRate === 'number' &&
			//Android chrome currently does not support playback rate
			!userAgentUtil.isAndroidChrome;
	}

	private isNativeVideoElement(): boolean {
		return !this.vgApi.isShimmedMediaElement();
	}

	private onActiveSourceChange(): void {
		this.updateVideoSource(this.activeSource);
	}

	private onPlaybackOptionsChange(): void {
		this.setPlaybackRate(1);
	}

	public playbackRatesTrackByFunc(index: number) {
		return index;
	}

	public setPlaybackRate(val: number): void {
		this.vgApi.setPlayback(val || 1);
	}

	public onSelectPlaybackRate(val: number): void {
		this.setPlaybackRate(val);
		this.hideMenu();
	}

	private sourceSupportsPlaybackRate(source: any): boolean {
		return source.type === 'video/mp4' ||
			this.HlsUtilService.isHLS(source.src, source.type);
	}

	private updateVideoSource(source?: any): void {
		const playbackRateEnabled = this.browserSupportsPlaybackRate &&
			source &&
			!source.isLive &&
			this.isNativeVideoElement() &&
			this.sourceSupportsPlaybackRate(source);

		this.playbackRateEnabledSubject$.next(playbackRateEnabled);

		if (playbackRateEnabled) {
			this.applyPlaybackRate();
		}
	}
}
